<script lang="ts">
export default {
  name: 'ViewFilter',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick, reactive } from 'vue';
import { Filter, X } from '@lucide/vue';
import { FilterField, FilterFieldOption, FilterMoreOptions, FilterRange, FilterValueType } from '@/types/ViewFilter.js';
import { LabelFilterType } from '@/types/components/FilterType.js';
import NumberInput from '@/components/v2/NumberInput.vue';
import { compactNumber } from '@/utilities/Filters.js';

interface _LabelValue<FilterValueType> extends LabelFilterType<FilterValueType> {
  category: string;
  range?: FilterRange;
}

interface _FilterField<FilterValueType> extends FilterField<FilterValueType> {
  values: _LabelValue<FilterValueType>[];
}

const props = withDefaults(
  defineProps<{
    appliedFilters?: FilterField<FilterValueType>[];
    appliedOptions?: FilterMoreOptions;
    filters: FilterField<FilterValueType>[];
    showMoreOptions?: boolean;
    showFavoritesOption?: boolean;
    filterOpen?: boolean;
    activeFilterCategory?: string;
    customActiveFilterBadges?: boolean;
  }>(),
  {
    appliedFilters: undefined,
    showMoreOptions: false,
    showFavoritesOption: false,
    customActiveFilterBadges: false,
  },
);

const emit = defineEmits<{
  change: [fields: FilterField<FilterValueType>[], moreOptions?: FilterMoreOptions];
  filterClosed: [];
}>();

// State
const menu = ref(false);
const searchQuery = ref('');
const localSelectedFields = ref<_LabelValue<FilterValueType>[]>([]);
const localSelectedOptions = ref<FilterFieldOption[]>([]);
const dirty = ref(false);
const activeCategory = ref('');
const localFilters = ref<_FilterField<FilterValueType>[]>([]);

const moreOptions = reactive({
  daysUntilExpiration: undefined as number | undefined,
  runoff: false,
  noRunoff: false,
  primary: false,
  excess: false,
  favorites: false,
} as FilterMoreOptions);

const activeRange = reactive({
  min: 0,
  max: 0,
});

// Computed
const selectedFields = computed(() => props.appliedFilters || []);

const appliedOptionsArray = computed(() => {
  if (!props.appliedOptions) return [];

  return Object.values(props.appliedOptions).filter((o) => o !== undefined);
});

const selectedCount = computed(() => {
  const valueCount = selectedFields.value.reduce((count, field) => count + field.values.length, 0);
  const rangeCount = selectedFields.value.reduce((count, field) => count + (field.range ? 1 : 0), 0);

  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;
  if (moreOptions.favorites) moreOptionsCount++;

  return valueCount + rangeCount + moreOptionsCount;
});

const localSelectedCount = computed(() => {
  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;
  if (moreOptions.favorites) moreOptionsCount++;

  return localSelectedFields.value.length + moreOptionsCount;
});

const categories = computed(() => {
  return [...new Set(props.filters.map((f) => f.category))];
});

const activeFilter = computed(() => localFilters.value.find((f) => f.category === activeCategory.value));

const activeFilterDescription = computed(() => activeFilter.value?.description ?? '');

const activeFilterOptions = computed(() => activeFilter.value?.options || []);

const activeFilterPrefix = computed(() => activeFilter.value?.prefix ?? '');

const filteredActiveFilterValues = computed(() => {
  const values = activeFilter.value?.values || [];

  if (!searchQuery.value) return values || [];

  const query = searchQuery.value.toLowerCase();
  return values.filter((field) => field.label.toLowerCase().includes(query));
});

const filteredActiveSelectedCount = computed(() => {
  const visibleValues = new Set(filteredActiveFilterValues.value.map((v) => v.value));
  return localSelectedFields.value.filter((f) => f.category === activeCategory.value && visibleValues.has(f.value)).length;
});

const isActiveAllSelected = computed(
  () => filteredActiveFilterValues.value.length > 0 && filteredActiveSelectedCount.value === filteredActiveFilterValues.value.length,
);

const isActiveSomeSelected = computed(() => filteredActiveSelectedCount.value > 0 && !isActiveAllSelected.value);

const isActiveFilterRange = computed(() => {
  if (activeFilter.value) {
    return activeFilter.value.range !== undefined;
  }
  return false;
});

const isMoreOptionsActive = computed(() => {
  return activeCategory.value === 'More Options';
});

const moreOptionsCount = computed(() => {
  let moreOptionsCount = 0;
  if (moreOptions.daysUntilExpiration !== undefined) moreOptionsCount++;
  if (moreOptions.runoff) moreOptionsCount++;
  if (moreOptions.noRunoff) moreOptionsCount++;
  if (moreOptions.primary) moreOptionsCount++;
  if (moreOptions.excess) moreOptionsCount++;
  if (moreOptions.favorites) moreOptionsCount++;
  return moreOptionsCount;
});

// Lifecycle

onMounted(() => {
  updateLocalFilters(props.filters);
  updateLocalAppliedFilters();
  updateLocalAppliedFilterOptions();
  updateLocalAppliedMoreOptions();

  searchQuery.value = '';
  activeCategory.value = categories.value[0] || '';
});

// Refs

const DROPDOWN_OFFSET = 4;
const searchInputRef = ref<HTMLElement | null>(null);
const activatorRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const dropdownPosition = ref({ top: 0, left: 0 });
let clickOutsideCleanup: (() => void) | null = null;

function updateDropdownPosition() {
  const el = activatorRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const menuMaxHeight = Math.min(540, viewportHeight * 0.7);
  const spaceBelow = viewportHeight - rect.bottom - DROPDOWN_OFFSET;
  const spaceAbove = rect.top - DROPDOWN_OFFSET;

  let top: number;
  if (spaceBelow >= menuMaxHeight || spaceBelow >= spaceAbove) {
    top = rect.bottom + DROPDOWN_OFFSET;
  } else {
    top = rect.top - menuMaxHeight - DROPDOWN_OFFSET;
  }
  top = Math.max(DROPDOWN_OFFSET, Math.min(top, viewportHeight - menuMaxHeight - DROPDOWN_OFFSET));

  const menuWidth = 650;
  let left = rect.left;
  left = Math.max(DROPDOWN_OFFSET, Math.min(left, window.innerWidth - menuWidth - DROPDOWN_OFFSET));

  dropdownPosition.value = { top, left };
}

// Watch
watch(
  () => props.filterOpen,
  (val) => {
    menu.value = val;
  },
);

watch(
  () => props.activeFilterCategory,
  (val) => {
    if (!val) {
      activeCategory.value = categories.value.length > 0 ? categories.value[0] : '';
    } else {
      activeCategory.value = val;
    }
  },
);

watch(menu, (val) => {
  if (!val) {
    dropdownPosition.value = { top: 0, left: 0 };
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
    emit('filterClosed');
    activeCategory.value = categories.value[0];
    return;
  }

  updateLocalAppliedFilters();
  updateLocalAppliedFilterOptions();
  updateLocalAppliedMoreOptions();
  searchQuery.value = '';

  nextTick(() => {
    updateDropdownPosition();
    const handleClickOutside = (e: MouseEvent) => {
      const el = e.target as Node;
      if (menuRef.value?.contains(el) || activatorRef.value?.contains(el)) return;
      menu.value = false;
    };
    const handleScroll = (e: Event) => {
      if (menuRef.value?.contains(e.target as Node)) return;
      menu.value = false;
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', handleScroll, true);
    };
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('scroll', handleScroll, true);
  });
});

watch(searchInputRef, () => {
  if (!isActiveFilterRange.value) {
    searchInputRef.value?.focus();
  }
});

watch(localSelectedFields, () => {
  dirty.value = true;
});

watch(localSelectedOptions, () => {
  dirty.value = true;
});

watch(
  () => props.filters,
  (next) => {
    updateLocalFilters(next);
    updateLocalAppliedFilters();
    updateLocalAppliedFilterOptions();
  },
);

watch(
  () => props.appliedFilters,
  () => {
    updateLocalAppliedFilters();
    updateLocalAppliedFilterOptions();
  },
);

watch(
  () => props.appliedOptions,
  () => {
    updateLocalAppliedMoreOptions();
  },
);

watch(activeCategory, () => {
  if (isActiveFilterRange.value) {
    const cat = localSelectedFields.value.find((f) => f.category === activeCategory.value);
    const defaultRange = localFilters.value?.find((f) => f.category === activeCategory.value)?.range;

    activeRange.min = cat?.range?.[0] ?? defaultRange?.[0] ?? 0;
    activeRange.max = cat?.range?.[1] ?? defaultRange?.[1] ?? 0;
  } else {
    searchInputRef.value?.focus();
  }
});

function updateLocalFilters(filtersToWrap: FilterField<FilterValueType>[]) {
  localFilters.value =
    filtersToWrap?.map((f) => ({
      category: f.category,
      description: f.description,
      values: f.values?.map((v) => ({ ...v, category: f.category })) ?? [],
      range: f.range,
      options: f.options,
      prefix: f.prefix,
    })) ?? [];
}

function updateLocalAppliedFilters() {
  localSelectedFields.value = selectedFields.value.flatMap((f) => {
    const availableValues = localFilters.value.find((filter) => filter.category === f.category)?.values || [];

    const selectedValues =
      f.values?.map((appliedValue) => {
        // Find the matching object from available options
        return availableValues.find((available) => available.value === appliedValue.value) || { ...appliedValue, category: f.category };
      }) || [];

    // Handle range filters...
    if (f.range) {
      selectedValues.push({
        label: '',
        value: 0,
        category: f.category,
        range: f.range,
      });
    }

    return selectedValues;
  });
}

function updateLocalAppliedFilterOptions() {
  localSelectedOptions.value = selectedFields.value.flatMap(
    (f) =>
      f.options?.map((o) => ({
        ...o,
        category: f.category,
      })) || [],
  );
}

function updateLocalAppliedMoreOptions() {
  if (!props.appliedOptions) {
    moreOptions.daysUntilExpiration = undefined;
    moreOptions.runoff = undefined;
    moreOptions.noRunoff = undefined;
    moreOptions.primary = undefined;
    moreOptions.excess = undefined;
    moreOptions.favorites = undefined;

    return;
  }

  moreOptions.daysUntilExpiration = props.appliedOptions.daysUntilExpiration;
  moreOptions.runoff = props.appliedOptions.runoff;
  moreOptions.noRunoff = props.appliedOptions.noRunoff;
  moreOptions.primary = props.appliedOptions.primary;
  moreOptions.excess = props.appliedOptions.excess;
  // Never carry favorites into a view that doesn't offer the option — it would count
  // toward the badge with no rendered checkbox to clear it.
  moreOptions.favorites = props.showFavoritesOption ? props.appliedOptions.favorites : undefined;
}

function updateActiveRangeMin(value: number) {
  activeRange.min = value;
  updateActiveCategoryRange();
}

function updateActiveRangeMax(value: number) {
  activeRange.max = value;
  updateActiveCategoryRange();
}

function updateActiveCategoryRange() {
  const filter = activeFilter.value;
  if (!filter) return;
  if (!filter.range) return;

  const selected = localSelectedFields.value.find((f) => f.category === activeCategory.value);

  if (selected) {
    selected.range = [activeRange.min, activeRange.max];
  } else {
    localSelectedFields.value.push({
      label: '',
      value: 0,
      category: filter.category,
      range: [activeRange.min, activeRange.max],
    });
  }

  // find in applied filters (or compare with default values), if the values match, then this range is no longer "dirty"

  let val: FilterRange;

  const passedIn = selectedFields.value.find((f) => f.category === activeCategory.value);
  if (!passedIn || !passedIn.range) {
    val = filter.range;
  } else {
    val = passedIn.range;
  }

  if (val[0] === activeRange.min && val[1] === activeRange.max) {
    // set value matches already applied filter or default range values -> remove it
    const index = localSelectedFields.value.findIndex((f) => f.category === activeCategory.value);
    if (index >= 0) {
      localSelectedFields.value.splice(index, 1);
    }
  }
}

function resetToDefaults() {
  localSelectedFields.value = [];

  moreOptions.daysUntilExpiration = undefined;
  moreOptions.runoff = false;
  moreOptions.noRunoff = false;
  moreOptions.primary = false;
  moreOptions.excess = false;
  moreOptions.favorites = false;

  // Reset active range to default values if current filter is a range filter
  if (isActiveFilterRange.value) {
    const defaultRange = localFilters.value?.find((f) => f.category === activeCategory.value)?.range;
    activeRange.min = defaultRange?.[0] ?? 0;
    activeRange.max = defaultRange?.[1] ?? 0;
  }
}

function clearFilteredActiveValues() {
  const visibleValues = new Set(filteredActiveFilterValues.value.map((v) => v.value));
  localSelectedFields.value = localSelectedFields.value.filter(
    (f) => f.category !== activeCategory.value || !visibleValues.has(f.value),
  );
}

function toggleSelectAllFilteredActiveValues() {
  if (isActiveAllSelected.value) {
    clearFilteredActiveValues();
    return;
  }

  const selectedValues = new Set(
    localSelectedFields.value.filter((f) => f.category === activeCategory.value).map((f) => f.value),
  );
  const unselected = filteredActiveFilterValues.value.filter((v) => !selectedValues.has(v.value));
  localSelectedFields.value = [...localSelectedFields.value, ...unselected];
}

function isEveryValueSelected(filter: FilterField<FilterValueType>) {
  const allValues = localFilters.value.find((f) => f.category === filter.category)?.values ?? [];
  if (allValues.length === 0) return false;

  const selectedValues = new Set(filter.values.map((v) => v.value));
  return allValues.every((v) => selectedValues.has(v.value));
}

function getCategorySelectionCount(category: string) {
  return localSelectedFields.value.filter((f) => f.category === category).length;
}

function cancel() {
  localSelectedFields.value = selectedFields.value.flatMap((f) =>
    f.values.map((g) => ({
      ...g,
      category: f.category,
      selected: false,
    })),
  );
  searchQuery.value = '';
  activeCategory.value = categories.value[0] || '';
  menu.value = false;
}

function apply() {
  if (!dirty.value) return;

  // reconstruct structure from flattened localSelectedFields

  const filterMap = new Map<string, FilterField<FilterValueType>>();

  const categories = localSelectedFields.value.reduce((categories, f) => categories.add(f.category), new Set<string>());

  categories.forEach((c) => {
    filterMap.set(c, {
      category: c,
      values: [],
    });
  });

  // process selectable filters

  localSelectedFields.value.forEach((selected: _LabelValue<FilterValueType>) => {
    const f = filterMap.get(selected.category);

    if (f) {
      if (selected.range) {
        f.range = selected.range;
      } else {
        f.values.push({
          label: selected.label,
          value: selected.value,
        });
      }
    }
  });

  // process options

  localSelectedOptions.value.forEach((option: FilterFieldOption) => {
    const f = filterMap.get(option.category);

    if (f) {
      if (!f.options) f.options = [];
      f.options?.push(option);
    }
  });

  // Selecting every value is "no filter" so records with a blank value in that field aren't excluded.
  const changed = Array.from(filterMap.values()).filter((f) => !isEveryValueSelected(f));

  let filterOptions = {} as FilterMoreOptions;
  filterOptions.daysUntilExpiration = moreOptions.daysUntilExpiration;
  filterOptions.runoff = moreOptions.runoff ? true : undefined;
  filterOptions.noRunoff = moreOptions.noRunoff ? true : undefined;
  filterOptions.primary = moreOptions.primary ? true : undefined;
  filterOptions.excess = moreOptions.excess ? true : undefined;
  filterOptions.favorites = props.showFavoritesOption && moreOptions.favorites ? true : undefined;

  let isFilterOptionsUndefined = false;
  if (!Object.values(filterOptions).some((value) => value !== undefined && value !== null && value !== false)) {
    isFilterOptionsUndefined = true;
  }
  emit('change', changed, isFilterOptionsUndefined ? undefined : filterOptions);

  menu.value = false;
}

function handleActiveFilterSelect(category: string) {
  activeCategory.value = category;
  menu.value = true;
}

function handleActiveMoreOptionsSelect() {
  activeCategory.value = 'More Options';
  menu.value = true;
}

function handleRemoveFiltersByCategory(category: string) {
  localSelectedFields.value = localSelectedFields.value.filter((f) => f.category !== category);
  apply();
}

function handleRemoveMoreOptions() {
  const optionKeys = Object.keys(moreOptions);
  for (const key of optionKeys) {
    moreOptions[key] = undefined;
  }
  apply();
}

function calculateFilterGroupCount(filterGroup: FilterField<FilterValueType>) {
  // For range type filters, there is nothing to count, just return a 1 to indicate the active range filter.
  if (filterGroup.range && filterGroup.range.length > 0) {
    return 1;
  } else {
    return filterGroup.values.length;
  }
}

onUnmounted(() => {
  clickOutsideCleanup?.();
});
</script>

<template>
  <div class="modern-filter-button flex gap-4 p-1">
    <label
      ref="activatorRef"
      tabindex="0"
      class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 select-none hover:bg-gray-50"
      data-testid="view-filter-activator"
      @click.prevent.stop="menu = !menu"
      @keydown.enter.space.prevent="menu = !menu">
      <Filter class="h-4 w-4 stroke-primary" />
      <span class="text-sm font-medium">Filters</span>
      <span
        v-if="selectedCount > 0"
        class="badge border-gray-400 bg-base-200 badge-sm"
        >{{ selectedCount }}</span>
    </label>

    <Teleport to="body">
      <Transition name="filter-popup-slide">
        <div
          v-if="menu"
          ref="menuRef"
          class="mint-filter-popup fixed z-9999 flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl"
          :style="{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }"
          @click.stop>
        <!-- Header -->
        <div class="flex shrink-0 items-center justify-between border-b border-gray-200 p-4">
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-semibold">Filters</h3>
            <div class="min-w-9">
              <span
                class="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-solid border-gray-400 bg-base-200 px-1.5 text-xs font-medium"
                :class="{
                  'invisible': localSelectedCount === 0,
                }">
                {{ localSelectedCount }}
              </span>
            </div>
          </div>

          <!-- Search bar -->
          <div class="w-full px-20">
            <div class="relative">
              <svg
                class="absolute top-2.5 left-3 h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                ref="searchInputRef"
                type="text"
                v-model="searchQuery"
                placeholder="Search"
                class="w-full rounded-md border border-solid border-gray-300 bg-gray-50 py-2 pr-3 pl-9 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                data-testid="column-filter-search" />
            </div>
          </div>

          <div class="flex items-center gap-4">
            <a
              @click="resetToDefaults"
              class="cursor-pointer text-sm text-blue-600 hover:underline"
              data-testid="column-filter-reset">
              Reset
            </a>
          </div>
        </div>

        <div class="flex min-h-0 flex-1 overflow-hidden">
          <!-- Left sidebar -->
          <div
            data-testid="view-filter-category"
            class="w-48 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50 select-none">
            <div class="p-3">
              <div
                v-for="stack in categories"
                :data-testid="`view-filter-category-${stack}`"
                :key="stack"
                @click="activeCategory = stack"
                class="cursor- mb-1 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm"
                :class="[activeCategory === stack ? 'bg-blue-100 font-medium text-blue-700' : 'text-gray-700 hover:bg-gray-100']">
                <span>{{ stack }}</span>
                <span
                  v-if="getCategorySelectionCount(stack) > 0"
                  class="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-solid border-gray-400 bg-base-200 px-1.5 text-xs font-medium">
                  {{ getCategorySelectionCount(stack) }}
                </span>
              </div>
              <div
                v-if="props.showMoreOptions"
                @click="activeCategory = 'More Options'"
                class="cursor- mb-1 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm"
                :class="[
                  activeCategory === 'More Options' ? 'bg-blue-100 font-medium text-blue-700' : 'text-gray-700 hover:bg-gray-100',
                ]">
                <span>More Options</span>
                <span
                  v-if="moreOptionsCount > 0"
                  class="inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-solid border-gray-400 bg-base-200 px-1.5 text-xs font-medium">
                  {{ moreOptionsCount }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right content -->
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              v-if="activeFilterOptions.length > 0"
              class="flex items-center justify-between border-b border-gray-200 p-4">
              Options
              <div class="flex items-center gap-2">
                <div
                  v-for="option in activeFilterOptions"
                  :key="option.label"
                  class="flex items-center gap-2">
                  <input
                    type="checkbox"
                    v-model="localSelectedOptions"
                    :value="option"
                    class="toggle border border-solid border-gray-400 toggle-primary toggle-sm" />
                  <label
                    for="includeChildren"
                    class="text-sm">
                    {{ option.label }}
                  </label>
                </div>
              </div>
            </div>

            <!-- Content area -->
            <div class="flex-1 overflow-y-auto p-4">
              <template v-if="isActiveFilterRange">
                <div>
                  {{ activeFilterDescription }}
                </div>

                <div class="mt-8 flex w-1/2 flex-col gap-8">
                  <NumberInput
                    label="Minimum"
                    data-testid="view-filter-range-min"
                    :modelValue="activeRange.min"
                    :prefix="activeFilterPrefix"
                    :hint="compactNumber(activeRange.min)"
                    @update:modelValue="(val) => updateActiveRangeMin(Number(val ?? 0))" />

                  <NumberInput
                    label="Maximum"
                    data-testid="view-filter-range-max"
                    :modelValue="activeRange.max"
                    :prefix="activeFilterPrefix"
                    :hint="compactNumber(activeRange.max)"
                    @update:modelValue="(val) => updateActiveRangeMax(Number(val ?? 0))" />
                </div>
              </template>

              <template v-if="isMoreOptionsActive">
                <label class="flex flex-col gap-3 rounded-md p-2 select-none">
                  <span class="font-bold">Days Until Expiration</span>
                  <input
                    type="text"
                    v-model="moreOptions.daysUntilExpiration"
                    class="input input-sm w-20 border border-solid border-gray-300" />
                </label>

                <div class="divider my-2"></div>

                <div class="font-bold">Runoff</div>

                <div class="flex">
                  <label class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.runoff"
                      class="h-4 w-4 rounded border-gray-300" />

                    <span class="text-sm text-gray-700 select-none"> Runoff </span>
                  </label>

                  <label class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.noRunoff"
                      class="h-4 w-4 rounded border-gray-300" />

                    <span class="text-sm text-gray-700 select-none"> No Runoff </span>
                  </label>
                </div>

                <div class="divider"></div>

                <div class="font-bold select-none">Primary / Excess</div>

                <div class="flex">
                  <label class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.primary"
                      class="h-4 w-4 rounded border-gray-300" />

                    <div class="flex flex-1 items-center gap-2">
                      <span class="text-sm text-gray-700 select-none"> Primary </span>
                    </div>
                  </label>

                  <label class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      v-model="moreOptions.excess"
                      class="h-4 w-4 rounded border-gray-300" />

                    <div class="flex flex-1 items-center gap-2">
                      <span class="text-sm text-gray-700 select-none"> Excess </span>
                    </div>
                  </label>
                </div>

                <template v-if="props.showFavoritesOption">
                  <div class="divider"></div>

                  <div class="font-bold select-none">Favorites</div>

                  <div class="flex">
                    <label class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        v-model="moreOptions.favorites"
                        data-testid="view-filter-favorites"
                        class="h-4 w-4 rounded border-gray-300" />

                      <div class="flex flex-1 items-center gap-2">
                        <span class="text-sm text-gray-700 select-none"> Show Only Favorite Accounts </span>
                      </div>
                    </label>
                  </div>
                </template>
              </template>

              <template v-else>
                <div
                  v-if="!isActiveFilterRange && filteredActiveFilterValues.length > 0"
                  class="mb-4 flex items-center justify-between">
                  <label class="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      :checked="isActiveAllSelected"
                      :indeterminate="isActiveSomeSelected"
                      @change="toggleSelectAllFilteredActiveValues"
                      data-testid="view-filter-select-all"
                      class="h-4 w-4 rounded border-gray-300" />
                    <span class="text-sm text-gray-700 select-none">Select all</span>
                  </label>
                  <a
                    v-if="filteredActiveSelectedCount > 0"
                    @click="clearFilteredActiveValues"
                    class="cursor-pointer text-sm text-blue-600 hover:underline"
                    data-testid="view-filter-clear">
                    Clear
                  </a>
                </div>

                <!-- Fields list -->
                <label
                  v-for="field in filteredActiveFilterValues"
                  :key="field.value"
                  class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    v-model="localSelectedFields"
                    :value="field"
                    class="h-4 w-4 rounded border-gray-300" />
                  <div class="flex flex-1 items-center gap-2">
                    <span class="text-sm text-gray-700">{{ field.label }}</span>
                  </div>
                </label>

                <!-- No results -->
                <div
                  v-if="!isActiveFilterRange && filteredActiveFilterValues.length === 0"
                  class="py-8 text-center text-gray-500">
                  No matches found
                </div>
              </template>
            </div>

            <!-- Footer -->
            <div class="flex shrink-0 justify-end gap-3 border-t border-gray-200 p-4">
              <button
                @click="cancel"
                class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                @click="apply"
                data-testid="view-filter-apply"
                class="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white opacity-90 transition-colors hover:opacity-100">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
      </Transition>
    </Teleport>
    <slot
      v-if="!customActiveFilterBadges"
      name="activeFilters"
      :appliedFilters="appliedFilters"
      :appliedOptions="moreOptions">
      <div
        v-for="filterGroup in appliedFilters"
        :key="filterGroup.category"
        @click="handleActiveFilterSelect(filterGroup.category)"
        class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-secondary bg-white px-3 py-1.5 whitespace-nowrap transition-all select-none hover:bg-gray-50"
        :data-testid="`view-filter-badge-${filterGroup.category}`">
        {{ filterGroup.category }}
        <div class="badge border-gray-400 bg-base-200 badge-sm">
          {{ calculateFilterGroupCount(filterGroup) }}
        </div>
        <X
          :size="15"
          class="stroke-gray-600 transition-colors hover:stroke-red-500"
          @click.stop="handleRemoveFiltersByCategory(filterGroup.category)" />
      </div>
      <div
        v-if="appliedOptionsArray.length > 0"
        @click="handleActiveMoreOptionsSelect()"
        class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-secondary bg-white px-3 py-1.5 transition-all select-none hover:bg-gray-50">
        More Options
        <div class="badge border-gray-400 bg-base-200">{{ appliedOptionsArray.length }}</div>
        <X
          :size="15"
          class="stroke-gray-600 transition-colors hover:stroke-red-500"
          @click.stop="handleRemoveMoreOptions()" />
      </div>
    </slot>
  </div>
</template>

<style scoped>
.modern-filter-button {
  display: inline-block;
}

.mint-filter-popup {
  width: 650px;
}

/* Slide in from the right */
.filter-popup-slide-enter-active,
.filter-popup-slide-leave-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}

.filter-popup-slide-enter-from,
.filter-popup-slide-leave-to {
  transform: translateX(-1rem);
  opacity: 0;
}

.filter-popup-slide-enter-to,
.filter-popup-slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}

/* Customize checkbox appearance */
input[type='checkbox'] {
  cursor: pointer;
}

input[type='checkbox']:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type='checkbox']:indeterminate {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 16 16'%3e%3cpath d='M5 8h6'/%3e%3c/svg%3e");
  background-color: #3b82f6;
  border-color: #3b82f6;
}
</style>
